import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SignUpForm } from '@models/form/form.models';
import { PersonEditRequest } from '@models/requests/person-edit-request.model';
import {
  EmailEditRequest,
  NotificationPreference2,
  PersonPhoneNumberRequest,
  PersonType,
  UserCustomerCreateRequest,
} from '@models/requests/user-customer-create-request.model';
import { Observable } from 'rxjs';
import { BookingSearchResult, User } from 'src/app/models/user.model';
import { ApiResponse } from 'src/app/models/responses/api-response.model';
import { EnvironmentService } from '../environment-service/environment.service';
import { PhoneNumberType } from '@models/booking.models';
import { LoginResponse } from '@models/responses/login-response.model';
import { DateTime } from 'luxon';
import { DATE_FORMAT_DEFAULT } from '@constants/date-format.constants';
import { PassengerTravelDocument } from '@models/requests/passenger-update-request.model';
import { VerifyRequest } from '@models/sms-validation.model';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private static readonly CONTROLLER_ROOT = 'v1/user';

  constructor(private httpClient: HttpClient) {}

  login(
    username: string,
    password: string,
    zQgfzoseqoiu: number,
  ): Observable<ApiResponse<LoginResponse>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${UserService.CONTROLLER_ROOT}/login`;
    return this.httpClient.post<ApiResponse<LoginResponse>>(endpoint, {
      username,
      password,
      zQgfzoseqoiu,
    });
  }

  verify(request: VerifyRequest): Observable<ApiResponse<LoginResponse>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${UserService.CONTROLLER_ROOT}/verify`;
    return this.httpClient.post<ApiResponse<LoginResponse>>(endpoint, request);
  }

  updateUserPersonalInfo(
    request: PersonEditRequest,
  ): Observable<ApiResponse<null>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${UserService.CONTROLLER_ROOT}/person`;
    return this.httpClient.patch<ApiResponse<null>>(endpoint, request);
  }

  updateUserEmailAddress(
    emailKey: string,
    request: EmailEditRequest,
  ): Observable<ApiResponse<null>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${UserService.CONTROLLER_ROOT}/email/${emailKey}`;
    return this.httpClient.put<ApiResponse<null>>(endpoint, request);
  }

  updateUserPhoneNumber(
    phoneKey: string,
    request: PersonPhoneNumberRequest,
  ): Observable<ApiResponse<null>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${UserService.CONTROLLER_ROOT}/phonenumber/${phoneKey}`;
    return this.httpClient.put<ApiResponse<null>>(endpoint, request);
  }

  createUserPhoneNumber(
    request: PersonPhoneNumberRequest,
  ): Observable<ApiResponse<null>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${UserService.CONTROLLER_ROOT}/phonenumber`;
    return this.httpClient.post<ApiResponse<null>>(endpoint, request);
  }

  updateUserTravelDocument(
    request: PassengerTravelDocument,
    docKey?: string,
  ): Observable<ApiResponse<null>> {
    if (docKey) {
      const endpoint = `${EnvironmentService.environment.apiBaseUrl}${UserService.CONTROLLER_ROOT}/travel-document/${docKey}`;
      return this.httpClient.put<ApiResponse<null>>(endpoint, request);
    } else return this.createUserTravelDocument(request);
  }

  createUserTravelDocument(
    request: PassengerTravelDocument,
  ): Observable<ApiResponse<null>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${UserService.CONTROLLER_ROOT}/travel-document`;
    return this.httpClient.post<ApiResponse<null>>(endpoint, request);
  }

  resetPassword(
    username: string,
    culture: string,
  ): Observable<ApiResponse<string>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${UserService.CONTROLLER_ROOT}/password-reset`;
    return this.httpClient.post<ApiResponse<string>>(endpoint, {
      username,
      culture,
    });
  }

  completeResetPassword(
    newpassword: string,
    token: string,
  ): Observable<ApiResponse<LoginResponse>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${UserService.CONTROLLER_ROOT}/complete-reset`;
    return this.httpClient.post<ApiResponse<LoginResponse>>(endpoint, {
      newpassword,
      token,
    });
  }

  validateResetPasswordToken(token: string): Observable<ApiResponse<boolean>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${UserService.CONTROLLER_ROOT}/validate-reset`;
    return this.httpClient.post<ApiResponse<boolean>>(endpoint, {
      token,
    });
  }

  getLoggedInUser(): Observable<ApiResponse<User>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${UserService.CONTROLLER_ROOT}`;
    return this.httpClient.get<ApiResponse<User>>(endpoint);
  }

  getUserBookings(): Observable<ApiResponse<BookingSearchResult[]>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${UserService.CONTROLLER_ROOT}/bookings?searchbyCustomerNumber=true`;
    return this.httpClient.get<ApiResponse<BookingSearchResult[]>>(endpoint);
  }

  createUser(
    userCustomerCreateRequest: UserCustomerCreateRequest,
  ): Observable<ApiResponse<LoginResponse>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${UserService.CONTROLLER_ROOT}/create-and-login`;
    return this.httpClient.post<ApiResponse<LoginResponse>>(
      endpoint,
      userCustomerCreateRequest,
    );
  }

  transformFormDataToCreateUserRequest(
    form: SignUpForm,
  ): UserCustomerCreateRequest {
    return {
      password: form.password,
      username: form.email,
      sendRegistrationConfirmation: true,
      person: {
        type: PersonType.Customer,
        name: {
          first: form.firstName,
          last: form.lastName,
        },
        emailAddresses: [
          {
            email: form.email,
            default: true,
            type: 'P',
          },
        ],
        phoneNumbers: [
          {
            default: true,
            number: form.phoneNumber,
            type: PhoneNumberType.Mobile,
          },
        ],
        notificationPreference: form.receiveMarketingNotifications
          ? NotificationPreference2.Promotional
          : NotificationPreference2.None,
        details: {
          gender: Number(form.gender),
          dateOfBirth: DateTime.fromFormat(
            form.birthDate,
            DATE_FORMAT_DEFAULT,
            { zone: 'utc' },
          ).toISO(),
        },
      },
    };
  }
}
